import type { PlanNode, TreeSection } from '../types';
import { boundsOf } from './geometry';
import type { Box, XY } from './geometry';

/** A group the server holds no size for draws at this size. */
export const GROUP_DEFAULT = { width: 360, height: 220 };
/** The corner handle stops here, so the title bar always fits. */
export const GROUP_MIN = { width: 160, height: 100 };
/** The server refuses a side longer than this. */
export const GROUP_MAX = 20000;
/** The bubble's title bar; the side handles sit at its middle. */
export const GROUP_BAR = 40;
/** Room around a selection that becomes a group: sides and bottom, then the top, which holds the title bar. */
const PAD = 40;
const PAD_TOP = 56;

type Size = { width: number; height: number };

export const isGroup = (n: Pick<PlanNode, 'kind'>) => n.kind === 'group';

export const groupSize = (n: Pick<PlanNode, 'width' | 'height'>): Size => ({
  width: n.width ?? GROUP_DEFAULT.width,
  height: n.height ?? GROUP_DEFAULT.height,
});

export const clampGroupSize = (s: Size): Size => ({
  width: Math.min(GROUP_MAX, Math.max(GROUP_MIN.width, Math.round(s.width))),
  height: Math.min(GROUP_MAX, Math.max(GROUP_MIN.height, Math.round(s.height))),
});

/** Every group's size, as the store takes it alongside positions. */
export const groupSizesOf = (nodes: PlanNode[]): Record<string, Size> => {
  const out: Record<string, Size> = {};
  nodes.forEach((n) => {
    if (isGroup(n)) out[n.id] = groupSize(n);
  });
  return out;
};

export const childrenOf = (nodes: PlanNode[]): Map<string, string[]> => {
  const out = new Map<string, string[]>();
  nodes.forEach((n) => {
    if (!n.parent_id) return;
    const list = out.get(n.parent_id);
    if (list) list.push(n.id);
    else out.set(n.parent_id, [n.id]);
  });
  return out;
};

/** Everything a node holds, at any depth. The seen set guards a chain the server should never have let through. */
export const descendantsIn = (children: Map<string, string[]>, id: string): string[] => {
  const out: string[] = [];
  const seen = new Set<string>([id]);
  const stack = [...(children.get(id) ?? [])];
  while (stack.length > 0) {
    const next = stack.pop() as string;
    if (seen.has(next)) continue;
    seen.add(next);
    out.push(next);
    stack.push(...(children.get(next) ?? []));
  }
  return out;
};

/** Nesting depth per node: the tree's answer where it has one, else counted up the parent chain. */
export const depthsOf = (nodes: PlanNode[], sections: TreeSection[]): Map<string, number> => {
  const out = new Map<string, number>(sections.map((s) => [s.id, s.depth]));
  const parent = new Map(nodes.map((n) => [n.id, n.parent_id]));
  nodes.forEach((n) => {
    if (out.has(n.id)) return;
    let d = 0;
    let at = n.parent_id;
    while (at && d < nodes.length) {
      d += 1;
      at = parent.get(at) ?? null;
    }
    out.set(n.id, d);
  });
  return out;
};

/**
 * The innermost group whose box holds `point`: the deepest one, and the smaller
 * when two at one depth overlap. `exclude` keeps a node out of itself and out of
 * anything it holds.
 */
export const groupAt = (
  boxes: Record<string, Box & { group?: boolean }>,
  point: XY,
  depths: Map<string, number>,
  exclude: Set<string>,
): string | null => {
  let best: string | null = null;
  let bestDepth = -1;
  let bestArea = Infinity;
  Object.keys(boxes).forEach((id) => {
    const b = boxes[id];
    if (!b.group || exclude.has(id)) return;
    if (point.x < b.x || point.x > b.x + b.width || point.y < b.y || point.y > b.y + b.height) return;
    const d = depths.get(id) ?? 0;
    const area = b.width * b.height;
    if (d > bestDepth || (d === bestDepth && area < bestArea)) {
      best = id;
      bestDepth = d;
      bestArea = area;
    }
  });
  return best;
};

export const centerOf = (b: Box): XY => ({ x: b.x + b.width / 2, y: b.y + b.height / 2 });

/** The box a new group takes around these: their bounds, padded, the top padded more for the title bar. */
export const groupAround = (boxes: Box[]): Box | null => {
  const b = boundsOf(boxes);
  if (!b) return null;
  const size = clampGroupSize({ width: b.width + PAD * 2, height: b.height + PAD_TOP + PAD });
  return { x: Math.round(b.x - PAD), y: Math.round(b.y - PAD_TOP), ...size };
};
